import { supabase } from './supabase';
import gamesCatalog from '../games/catalog.json';
import { withGamesSection } from '../games/withGamesSection';
import { MOVIES_CATEGORY_ID, RECENTLY_ADDED_GENRE_ID } from './constants';

// Base URL for the public Supabase Storage bucket that holds all navigation images.
// Derived from the existing REACT_APP_SUPABASE_URL so no extra env var is needed.
const STORAGE_BASE =
  `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/thumbnails`;

/**
 * Converts a stored image_path like "/images/games.png" into a fully-qualified
 * Supabase Storage URL.  External URLs (http/https) are returned unchanged.
 */
const toImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath;       // already absolute
  const filename = imagePath.split('/').pop();              // "games.png"
  return `${STORAGE_BASE}/${filename}`;
};

// ── Menu items ────────────────────────────────────────────────

/**
 * Recursively builds the nested category tree from a flat
 * array of menu_item rows using the parent_id adjacency model.
 */
const buildTree = (rows, parentId = null) =>
  rows
    .filter(row => row.parent_id === parentId)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(row => {
      const item = {
        id: row.legacy_id,                        // backwards-compat id for routing + IL filter
        name: row.name,
        image: toImageUrl(row.image_path),
        type: row.type,
      };

      // Restore the multi-variant link object (Activity Guides) or plain string link
      if (row.link_config) {
        item.link = row.link_config;              // { default: '...', otterbein: '...' }
      } else if (row.link) {
        item.link = row.link;
      }

      // Facility-type gates
      if (row.show_only_with?.length) {
        item.showOnlyWith = row.show_only_with;
      }

      const children = buildTree(rows, row.id);
      if (children.length > 0) {
        item.subcategories = children;
      }

      return item;
    });

/**
 * Fetches all menu_items and returns the nested data structure
 * that App.js / MenuBrowser expect.
 *
 * Shape: { categories: [ { id, name, image, type, subcategories?, link?, showOnlyWith? } ] }
 */
export const fetchMenuData = async () => {
  const { data: rows, error } = await supabase
    .from('menu_items')
    .select('*')
    .order('sort_order');

  if (error) throw error;

  const categories = buildTree(rows);

  // Append virtual "Recently Added" genre to the Movies category so the
  // navigation tab shows it without needing a separate DB row.
  const moviesCategory = categories.find(c => c.id === MOVIES_CATEGORY_ID);
  if (moviesCategory) {
    moviesCategory.subcategories = [
      ...(moviesCategory.subcategories || []),
      {
        id: RECENTLY_ADDED_GENRE_ID,
        name: 'Recently Added',
        image: toImageUrl('/images/recent.png'),
        type: 'movies',
      },
    ];
  }

  // The Games section lives in the repo next to the games themselves.
  return { categories: withGamesSection(categories, gamesCatalog) };
};

// ── Movies ────────────────────────────────────────────────────

/**
 * Fetches all movie genres ordered by sort_order.
 * Returns raw genre rows; the caller is responsible for building
 * the image path using PUBLIC_URL.
 */
export const fetchMovieGenres = async () => {
  const { data: genres, error } = await supabase
    .from('movie_genres')
    .select('*')
    .order('sort_order');

  if (error) throw error;

  // Append the virtual "Recently Added" entry (backed by is_recently_added flag)
  // and convert all image_path values to Supabase Storage URLs up front.
  return [
    ...genres.map(g => ({ ...g, image_path: toImageUrl(g.image_path) })),
    { id: RECENTLY_ADDED_GENRE_ID, name: 'Recently Added', image_path: toImageUrl('/images/recent.png'), sort_order: 99 },
  ];
};

/**
 * Fetches movies for a given genre.
 * RECENTLY_ADDED_GENRE_ID is the virtual "Recently Added" genre — it queries by
 * is_recently_added flag instead of genre_id.
 */
export const fetchMoviesByGenre = async (genreId) => {
  const numericId = parseInt(genreId, 10);

  const query =
    numericId === RECENTLY_ADDED_GENRE_ID
      ? supabase.from('movies').select('*').eq('is_recently_added', true).order('sort_order')
      : supabase.from('movies').select('*').eq('genre_id', numericId).order('sort_order');

  const { data: movies, error } = await query;
  if (error) throw error;
  return movies;
};
